import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Grid,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import PreFilters from './PreFilters';
import { styled } from '@mui/material/styles';
import texts from 'texts';
import {
  translateKeyword,
  translation,
} from 'utils/translate';
import Tip from 'components/Tip';
import { useSearchParams } from 'react-router-dom';
import './RenderSearchForm.css';

const initialProximityValues = {
  term1: '',
  term2: '',
  slop: 1,
};

const RenderSearchForm = ({
  totalHits,
  formTypes,
  activeFormType,
  searchFormRef,
  handleBasicSearchSubmit,
  term,
  handleTermChange,
  handleSelectOnClick,
  config,
  loadingStatus,
  preFilters,
  handlePreFiltersSubmit,
  handlePrefilterDockGroupChange,
  resetSearch,
}) => {
  //Global states:
  const {
    lang,
    searchState,
    searchFormFullScreen,
    docGroup,
  } = useSelector((state) => ({
    searchState: state.searchState,
    lang: state.lang,
    searchFormFullScreen: state.searchFormFullScreen,
    docGroup: state.docGroup,
  }));
  const [searchParams, setSearchParams] = useSearchParams();
  // values for term1, term2 and slop for max distance
  const [proximityValues, setProximityValues] = useState(
    initialProximityValues,
  );

  const setOrAppendSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (newParams.has(key)) {
        newParams.set(key, value); // Replace existing value
      } else {
        newParams.append(key, value); // Add new key
      }
    });

    setSearchParams(newParams);
  };

  const setAppendOrRemoveByName = ({ set, remove }) => {
    const params = new URLSearchParams(searchParams);
    if (set) {
      Object.entries(set).forEach(([key, value]) => {
        if (params.has(key)) {
          params.set(key, value); // Replace existing value
        } else {
          params.append(key, value); // Add new key
        }
      });
    }
    if (remove) {
      params.delete(remove);
    }
    setSearchParams(params);
  };

  const handleProximitySearchSubmit = (proximityValue) => {
    const initialSearchParams = {
      docGroup: docGroup,
      sort: 'score',
      proximity: proximityValue,
      lang: lang,
      status: searchState,
    };

    let searchParams = {};
    // copy initial params into new object if they are not nullish
    // and stringify proximity params
    Object.entries(initialSearchParams).forEach(
      ([key, value]) => {
        if (
          key === 'proximity' &&
          value &&
          typeof value === 'object'
        ) {
          searchParams.proximity = JSON.stringify(value);
        } else if (value !== undefined && value !== null) {
          searchParams[key] = value;
        }
      },
    );
    setOrAppendSearchParams(searchParams);
  };

  const changeActiveFormType = (key) => {
    // Reset proximity searchParams when move to basic search
    // Reset basic searchParams when move to proximity search
    if (key === 'basic' && searchParams.has('proximity')) {
      setAppendOrRemoveByName({
        set: { activeFormType: key, docGroup: 'all' },
        remove: 'proximity',
      });
    } else {
      setOrAppendSearchParams({ activeFormType: key });
    }
  };

  if (loadingStatus === 'error') {
    return (
      <div>
        Hakukone poissa käytöstä hetkellisesti. Pahoittelut
        häiriöstä.
      </div>
    );
  }

  return (
    <>
      {searchState === 'tutkija' && (
        <div>
          <h2 className="section--info-area-h2">
            {translation('valitse_hakutapa', lang)}
          </h2>
        </div>
      )}
      <div
        className={
          searchState === 'yleinen'
            ? 'search-form-wrapper-for-everyone'
            : 'search-form-wrapper'
        }
      >
        {searchState === 'tutkija' && (
          <div
            className="form-type-select-area"
            ref={searchFormRef}
          >
            {formTypes.map((key) => (
              <Tip
                title={translation(
                  'search',
                  key,
                  'tooltip',
                  lang,
                )}
                key={key}
              >
                <button
                  type="button"
                  className={
                    activeFormType === key
                      ? 'form-type-select form-type-select--active'
                      : 'form-type-select'
                  }
                  onClick={() => changeActiveFormType(key)}
                >
                  {translation('search', key, 'name', lang)}
                </button>
              </Tip>
            ))}
          </div>
        )}

        {activeFormType === 'basic' ||
        activeFormType === 'semantic' ? (
          <>
            <form
              className="search-form search-form--basic"
              onSubmit={handleBasicSearchSubmit}
              ref={searchFormRef}
            >
              <p className="search-form--prompt">
                {translation('hae_lakitutkasta', lang)}
                <Tip
                  title={translation(
                    'texts_tooltip_basic',
                    lang,
                  )}
                >
                  <span
                    alt={texts.info.help}
                    className="tooltip-help tippable"
                  ></span>
                </Tip>
              </p>
              <div
                className={
                  searchState === 'yleinen'
                    ? 'search-form--select-docgroup-for-everyone'
                    : searchFormFullScreen
                      ? 'search-form--select-docgroup-fullscreen'
                      : 'search-form--select-docgroup'
                }
              >
                {searchState === 'tutkija' && (
                  <Grid
                    container
                    className="search-form-container"
                  >
                    <Grid item xs={12} sm={12} md={3}>
                      <Select
                        sx={{
                          borderLeft:
                            '2px solid var(--gray-dark)',
                          borderBottom:
                            '2px solid var(--gray-dark)',
                          borderRadius: '3px 0px 0px 3px',
                          padding: '10 14px',
                          width: '100%',
                          height: '92%',
                          backgroundColor:
                            'var(--light-gray)',
                          display: 'inline-block',
                          lineHeight: '1.1876em',
                        }}
                        value={docGroup || 'all'}
                        onChange={handleSelectOnClick}
                      >
                        {Object.entries(
                          config.docGroups,
                        ).map(
                          ([key, value]) =>
                            value.searchable && (
                              <MenuItem
                                value={key}
                                key={key}
                              >
                                {translation(
                                  value.name,
                                  lang,
                                )}
                              </MenuItem>
                            ),
                        )}
                      </Select>
                    </Grid>
                    <Grid
                      item
                      className="search-term-grid"
                      xs={12}
                      sm={12}
                      md={9}
                    >
                      <TextField
                        id="basic-search-term"
                        className="basic-search-term"
                        onChange={handleTermChange}
                        key={translation(
                          'doc_groups',
                          docGroup,
                          'search',
                          lang,
                        )}
                        label={translation(
                          'doc_groups',
                          docGroup,
                          'search',
                          lang,
                        )}
                        variant="outlined"
                        value={term ? term : ''}
                      />
                      <Button
                        aria-label="hae"
                        variant="outlined"
                        type="submit"
                        className="submit-basic-search"
                        disableTouchRipple
                      >
                        <SearchIcon />
                      </Button>
                    </Grid>
                  </Grid>
                )}
                {searchState === 'yleinen' && (
                  <>
                    <TextField
                      id="basic-search-term"
                      className={
                        searchState === 'yleinen'
                          ? 'basic-search-term-for-everyone'
                          : 'basic-search-term'
                      }
                      onChange={handleTermChange}
                      key={translation(
                        'doc_groups',
                        docGroup,
                        'search',
                        lang,
                      )}
                      label={translation(
                        'doc_groups',
                        docGroup,
                        'search',
                        lang,
                      )}
                      variant="outlined"
                      value={term ? term : ''}
                    />
                    <Button
                      aria-label="hae"
                      variant="outlined"
                      type="submit"
                      className="submit-basic-search"
                      disableTouchRipple
                    >
                      <SearchIcon />
                    </Button>
                  </>
                )}
              </div>
            </form>
          </>
        ) : activeFormType === 'proximity' ? (
          <ProximitySearch
            lang={lang}
            docGroup={docGroup}
            allDocGroups={config.docGroups}
            proximityValues={proximityValues}
            setProximityValues={setProximityValues}
            resetSearch={resetSearch}
            handleSelectOnClick={handleSelectOnClick}
            handleProximitySearchSubmit={
              handleProximitySearchSubmit
            }
          />
        ) : (
          <PreFilters
            key={docGroup}
            startValues={preFilters}
            docGroup={docGroup}
            term={term}
            handlePreFiltersSubmit={handlePreFiltersSubmit}
            handlePrefilterDockGroupChange={
              handlePrefilterDockGroupChange
            }
            handlePreFiltersClear={resetSearch}
            isPrefilterOpen={true}
          />
        )}
      </div>
      {(loadingStatus === 'loadingMore' ||
        loadingStatus === 'loading') && (
        <div className="circular-progress">
          <CircularProgress size={68} />
        </div>
      )}
    </>
  );
};

const StyledInverseButton = styled(Button)({
  backgroundColor: 'white',
  borderRadius: 5,
  border: '3px solid var(--primary)',
  height: '48px', // Add unit
  textTransform: 'none', // Moved from label
  color: 'var(--primary)', // Moved from label
  fontSize: '16px', // Moved from label
  '&:hover': {
    backgroundColor: 'var(--grey-dark)',
  },
});

const ProximitySearch = ({
  lang,
  docGroup,
  allDocGroups,
  proximityValues,
  setProximityValues,
  resetSearch,
  handleSelectOnClick,
  handleProximitySearchSubmit,
}) => {
  const handleValueChange = (event) => {
    const { target } = event;
    setProximityValues({
      ...proximityValues,
      [target.name]: target.value,
    });
  };

  const handleClear = () => {
    resetSearch();
    setProximityValues({});
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleProximitySearchSubmit(proximityValues);
  };

  return (
    <Grid container>
      <Grid item xs={12} sm={12} md={8}>
        <div className="proximity-doctype-div">
          <h4 className="proximity-doctype-h4">
            {translateKeyword('type', lang)}
            <Tip
              title={translation(
                'asiakirjatyyppi_tooltip',
                lang,
              )}
            >
              <span
                alt={translation(
                  'asiakirjatyyppi_tooltip',
                  lang,
                )}
                className="tooltip-help tippable"
              ></span>
            </Tip>
          </h4>
          <Select
            //className="docgroup-select"
            sx={{
              borderLeft: '2px solid var(--gray-dark)',
              borderBottom: '2px solid var(--gray-dark)',
              borderRadius: '3px 0px 0px 3px',
              padding: '10 14px',
              width: '70%',
              height: '92%',
              backgroundColor: 'var(--light-gray)',
              display: 'inline-block',
              lineHeight: '1.1876em',
            }}
            value={docGroup}
            onChange={handleSelectOnClick}
          >
            {Object.entries(allDocGroups).map(
              ([key, value]) =>
                value.searchable && (
                  <MenuItem value={key} key={key}>
                    {translation(value.name, lang)}
                  </MenuItem>
                ),
            )}
          </Select>
        </div>
        <div className="proximity-form-div">
          <form
            className="search-form search-form--proximity"
            onSubmit={handleSubmit}
          >
            <Grid container spacing={0.5}>
              <Grid item xs={12} sm={12} md={4}>
                <h4 className="proximity-labels">
                  {translation(
                    'search',
                    'proximity',
                    'first_term',
                    lang,
                  )}
                  <TextField
                    id="proximity-input-first"
                    className="proximity-search-input search-input--left"
                    name="term1"
                    value={proximityValues.term1}
                    onChange={handleValueChange}
                    variant="outlined"
                  />
                </h4>
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <h4 className="proximity-labels">
                  {translation(
                    'search',
                    'proximity',
                    'distance',
                    lang,
                  )}
                  <TextField
                    id="proximity-input-max"
                    className="proximity-search-input search-input--left"
                    name="slop"
                    value={proximityValues.slop}
                    onChange={handleValueChange}
                    type="number"
                    inputProps={{
                      min: '0',
                      max: '1000',
                      step: '1',
                    }}
                    variant="outlined"
                  />
                </h4>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <h4 className="proximity-labels">
                  {translation(
                    'search',
                    'proximity',
                    'second_term',
                    lang,
                  )}
                  <TextField
                    id="proximity-input-second"
                    className="proximity-search-input search-input--right"
                    name="term2"
                    value={proximityValues.term2}
                    onChange={handleValueChange}
                    variant="outlined"
                  />
                </h4>
              </Grid>
              <Grid
                item
                className="proximity-submit-grid"
                xs={12}
                sm={12}
                md={1}
              >
                <Button
                  aria-label="hae"
                  variant="outlined"
                  type="submit"
                  className="submit-basic-search"
                  disableTouchRipple
                >
                  <SearchIcon />
                </Button>
              </Grid>
              <StyledInverseButton
                style={{ marginTop: '0.5em' }}
                onClick={handleClear}
              >
                {translation(
                  'search',
                  'proximity',
                  'remove',
                  lang,
                )}
              </StyledInverseButton>
            </Grid>
          </form>
        </div>
      </Grid>
    </Grid>
  );
};

export default RenderSearchForm;
